import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  getStripeConnectInstance,
  getStripeRuntimeConfig,
  resetStripeConnectInstance,
} from '../../Services/stripeConnect';
import { useI18n } from '../../i18n/I18nProvider';

const StripeEmbeddedComponent = ({ component, onExit, onReady, minHeight }) => {
  const { language } = useI18n();
  const en = language === 'en';
  const L = (fr, english) => (en ? english : fr);
  const hostRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [isTestMode, setIsTestMode] = useState(false);
  const resolvedMinHeight = minHeight || ({
    'account-onboarding': '26rem',
    'account-management': '28rem',
    payouts: '17rem',
    payments: '17rem',
    documents: '15rem',
  }[component] || '18rem');

  useEffect(() => {
    let mounted = true;
    let element = null;

    setLoading(true);
    setError('');

    const mountStripe = async () => {
      try {
        const [{ mode }, connect] = await Promise.all([
          getStripeRuntimeConfig(),
          getStripeConnectInstance(component),
        ]);
        if (mounted) setIsTestMode(mode === 'test');
        if (!mounted) return;

        element = connect.create(component);

        element.setOnLoaderStart?.(() => {
          if (!mounted) return;
          setLoading(false);
          onReady?.();
        });

        element.setOnLoadError?.((loadError) => {
          if (!mounted) return;
          const errorType = String(loadError?.type || loadError?.error?.type || '').trim();
          const rawMessage = loadError?.error?.message || loadError?.message || '';
          const message = rawMessage
            ? `${rawMessage}${import.meta.env.DEV && errorType ? ` (${errorType})` : ''}`
            : L('Le module bancaire sécurisé n’a pas pu se charger. Vérifiez votre connexion puis réessayez.', 'The secure banking module could not load. Check your connection and try again.');
          setLoading(false);
          setError(message);
        });

        if (component === 'account-onboarding') {
          element.setCollectionOptions?.({ fields: 'eventually_due', futureRequirements: 'include' });
          element.setFullTermsOfServiceUrl?.(`${window.location.origin}/terms`);
          element.setPrivacyPolicyUrl?.(`${window.location.origin}/privacy`);
          element.setOnExit?.(() => onExit?.());
        }

        hostRef.current?.appendChild(element);
      } catch (nextError) {
        if (!mounted) return;
        setLoading(false);
        setError(nextError?.message || L('Impossible de démarrer le module bancaire sécurisé.', 'Unable to start the secure banking module.'));
      }
    };

    mountStripe();

    return () => {
      mounted = false;
      element?.remove?.();
    };
  }, [component, en, onExit, onReady, retryKey]);

  const retry = () => {
    resetStripeConnectInstance(component);
    setRetryKey((value) => value + 1);
  };

  return (
    <Wrapper $minHeight={resolvedMinHeight}>
      {component === 'account-onboarding' && isTestMode && (
        <TestHint>
          <strong>{L('Mode test du paiement', 'Payment test mode')}</strong>
          <span>{L('Cliquez sur « Utiliser le compte de test » pour remplir automatiquement un compte bancaire fictif.', 'Click “Use test account” to automatically fill in a fictitious bank account.')}</span>
        </TestHint>
      )}
      {loading && <Loading role="status">{L('Chargement de votre espace sécurisé…', 'Loading your secure space…')}</Loading>}
      {error && (
        <Error role="alert">
          <strong>{L('Le module sécurisé n’est pas disponible.', 'The secure module is unavailable.')}</strong>
          <span>{error}</span>
          <RetryButton type="button" onClick={retry}>{L('Réessayer', 'Try again')}</RetryButton>
        </Error>
      )}
      <StripeFrame>
        <Host ref={hostRef} aria-live="polite" />
      </StripeFrame>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: grid;
  gap: .75rem;
  min-width: 0;
  min-height: ${({ $minHeight }) => $minHeight};
  padding: clamp(.55rem, 1.5vw, .85rem);
  border: 1px solid #e4ddd3;
  border-radius: 1rem;
  background: #f8f6f2;

  @media (max-width: 42rem) {
    min-height: 18rem;
    padding: .35rem;
    border-radius: .85rem;
    background: #fff;
  }
`;

const StripeFrame = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 54rem;
  margin: 0 auto;
  padding: clamp(.7rem, 1.8vw, 1rem);
  box-sizing: border-box;
  border: 1px solid #e7e1d8;
  border-radius: .95rem;
  background: #fff;
  box-shadow: 0 .45rem 1.4rem rgba(17, 17, 17, .045);

  @media (max-width: 42rem) {
    padding: .35rem;
    border: 0;
    border-radius: .7rem;
    box-shadow: none;
  }
`;

const Host = styled.div`
  display: block;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  > * {
    display: block;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }
`;

const TestHint = styled.div`
  display: grid;
  gap: .15rem;
  max-width: 54rem;
  margin: 0 auto;
  padding: .7rem .85rem;
  border: 1px solid #e7ddc8;
  border-radius: .8rem;
  background: #fffaf0;
  color: #5f471d;
  font-size: .74rem;
  line-height: 1.45;

  strong { font-size: .75rem; }
  span { color: #71634a; }
`;

const Loading = styled.div`
  display: grid;
  place-items: center;
  min-height: 12rem;
  padding: 1rem;
  color: #6d665d;
  font-size: .82rem;
  text-align: center;
`;

const RetryButton = styled.button`
  justify-self: start;
  min-height: 2.5rem;
  margin-top: .35rem;
  padding: .55rem .8rem;
  border: 0;
  border-radius: 999px;
  background: #111;
  color: #fff;
  font: inherit;
  font-size: .75rem;
  font-weight: 800;
  cursor: pointer;
`;

const Error = styled.div`
  display: grid;
  gap: .25rem;
  margin: .25rem;
  padding: .85rem 1rem;
  border: 1px solid #efcfcb;
  border-radius: .85rem;
  background: #fff6f5;
  color: #8c332c;
  font-size: .78rem;
  line-height: 1.5;
`;

export default StripeEmbeddedComponent;
